import { useEffect } from 'react';
import { useControls } from 'leva';
import {
    FrontSide,
    BackSide,
    DoubleSide,
    NoBlending,
    NormalBlending,
    AdditiveBlending,
    SubtractiveBlending,
    MultiplyBlending,
    CustomBlending,
    AddEquation,
    SubtractEquation,
    ReverseSubtractEquation,
    MinEquation,
    MaxEquation,
    ZeroFactor,
    OneFactor,
    SrcColorFactor,
    OneMinusSrcColorFactor,
    SrcAlphaFactor,
    OneMinusSrcAlphaFactor,
    DstAlphaFactor,
    OneMinusDstAlphaFactor,
    DstColorFactor,
    OneMinusDstColorFactor,
    SrcAlphaSaturateFactor,
    ConstantColorFactor,
    OneMinusConstantColorFactor,
    ConstantAlphaFactor,
    OneMinusConstantAlphaFactor,
    NeverDepth,
    AlwaysDepth,
    EqualDepth,
    LessDepth,
    LessEqualDepth,
    GreaterEqualDepth,
    GreaterDepth,
    NotEqualDepth
} from "three";

export default function MaterialController({ name, material, onUpdate }) {
    const config = useControls(name, getMaterialConfig(material), { collapsed: true });

    // Invia il config aggiornato al parent (opzionale)
    useEffect(() => {
        onUpdate(name, config);
    }, [config, name, onUpdate]);

    function getMaterialConfig(material) {
        // Mesh standard material
        const baseConfig = {
            color: material.color ? `#${material.color.getHexString()}` : '#c9ffa1',
            transparent: material.transparent,
            opacity: { value: material.opacity || 1, min: 0, max: 1, step: 0.01 },
            emissive: material.emissive ? `#${material.emissive.getHexString()}` : '#000000',
            emissiveIntensity: { value: material.emissiveIntensity ? material.emissiveIntensity : 0, min: 0, max: 10, step: 0.01 },
            roughness: { value: material.roughness || 0.0, min: 0, max: 1, step: 0.01 },
            metalness: { value: material.metalness || 0.0, min: 0, max: 1, step: 0.01 },
            blending: {
                options: {
                    'NoBlending': NoBlending,
                    'NormalBlending': NormalBlending,
                    'AdditiveBlending': AdditiveBlending,
                    'SubtractiveBlending': SubtractiveBlending,
                    'MultiplyBlending': MultiplyBlending,
                    'CustomBlending': CustomBlending
                }, value: material.blending
            },
            blendEquation: {
                options: {
                    'AddEquation': AddEquation,
                    'SubtractEquation': SubtractEquation,
                    'ReverseSubtractEquation': ReverseSubtractEquation,
                    'MinEquation': MinEquation,
                    'MaxEquation': MaxEquation
                }, value: material.blendEquation
            },
            blendSrc: {
                options: {
                    'ZeroFactor': ZeroFactor,
                    'OneFactor': OneFactor,
                    'SrcColorFactor': SrcColorFactor,
                    'OneMinusSrcColorFactor': OneMinusSrcColorFactor,
                    'SrcAlphaFactor': SrcAlphaFactor,
                    'OneMinusSrcAlphaFactor': OneMinusSrcAlphaFactor,
                    'DstAlphaFactor': DstAlphaFactor,
                    'OneMinusDstAlphaFactor': OneMinusDstAlphaFactor,
                    'DstColorFactor': DstColorFactor,
                    'OneMinusDstColorFactor': OneMinusDstColorFactor,
                    'SrcAlphaSaturateFactor': SrcAlphaSaturateFactor,
                    'ConstantColorFactor': ConstantColorFactor,
                    'OneMinusConstantColorFactor': OneMinusConstantColorFactor,
                    'ConstantAlphaFactor': ConstantAlphaFactor,
                    'OneMinusConstantAlphaFactor': OneMinusConstantAlphaFactor,
                }, value: material.blendSrc
            },
            blendDst: {
                options: {
                    'ZeroFactor': ZeroFactor,
                    'OneFactor': OneFactor,
                    'SrcColorFactor': SrcColorFactor,
                    'OneMinusSrcColorFactor': OneMinusSrcColorFactor,
                    'SrcAlphaFactor': SrcAlphaFactor,
                    'OneMinusSrcAlphaFactor': OneMinusSrcAlphaFactor,
                    'DstAlphaFactor': DstAlphaFactor,
                    'OneMinusDstAlphaFactor': OneMinusDstAlphaFactor,
                    'DstColorFactor': DstColorFactor,
                    'OneMinusDstColorFactor': OneMinusDstColorFactor,
                    'ConstantColorFactor': ConstantColorFactor,
                    'OneMinusConstantColorFactor': OneMinusConstantColorFactor,
                    'ConstantAlphaFactor': ConstantAlphaFactor,
                    'OneMinusConstantAlphaFactor': OneMinusConstantAlphaFactor,
                }, value: material.blendDst
            },
            depthFunc: {
                options: {
                    'NeverDepth': NeverDepth,
                    'AlwaysDepth': AlwaysDepth,
                    'EqualDepth': EqualDepth,
                    'LessDepth': LessDepth,
                    'LessEqualDepth': LessEqualDepth,
                    'GreaterEqualDepth': GreaterEqualDepth,
                    'GreaterDepth': GreaterDepth,
                    'NotEqualDepth': NotEqualDepth
                }, value: material.depthFunc,
            },
            depthTest: material.depthTest,
            depthWrite: material.depthWrite,
            side: {
                options: {
                    'FrontSide': FrontSide,
                    'BackSide': BackSide,
                    'DoubleSide': DoubleSide
                }, value: material.side
            },
        }

        // Mesh physical material
        if (material.isMeshPhysicalMaterial) {

            return {
                // meshPhysicalMaterial: material.isMeshPhysicalMaterial || false,
                //transmissionSampler: material.transmissionSampler || false,
                //backside: material.backside || false,
                //samples: { value: material.samples || 10, min: 1, max: 32, step: 1 },
                //resolution: { value: material.resolution || 1024, min: 256, max: 2048, step: 256 },
                ...baseConfig,
                transmission: { value: material.transmission ? material.transmission : 0, min: 0, max: 1 },
                thickness: { value: material.thickness ? parseFloat(material.thickness.toFixed(3)) : 0, min: 0, max: 5, step: 0.001, precision: 3 },
                ior: { value: material.ior ? material.ior : 1, min: 1, max: 5, step: 0.01 },
                clearcoat: { value: material.clearcoat ? material.clearcoat : 0, min: 0, max: 1 },
                dispersion: { value: material.dispersion ? material.dispersion : 0, min: 0, max: 1, step: 0.001, precision: 3 },
                iridescence: { value: material.iridescence ? material.iridescence : 0, min: 0, max: 1, step: 0.01 },
                iridescenceIOR: { value: material.iridescenceIOR ? material.iridescenceIOR : 1.3, min: 1, max: 5, step: 0.01 },
                specularColor: material.specularColor ? `#${material.specularColor.getHexString()}` : '#ffffff',
                specularIntensity: { value: material.specularIntensity ? material.specularIntensity : 0, min: 0, max: 1, step: 0.01 },
                // chromaticAberration: { value: material.chromaticAberration || 0.06, min: 0, max: 1 },
                anisotropy: { value: material.anisotropy ? material.anisotropy : 0, min: 0, max: 1, step: 0.01 },
                // distortion: { value: material.distortion || 0.0, min: 0, max: 1, step: 0.01 },
                // distortionScale: { value: material.distortionScale || 0.3, min: 0.01, max: 1, step: 0.01 },
                // temporalDistortion: { value: material.temporalDistortion || 0, min: 0, max: 1, step: 0.01 },
                attenuationDistance: { value: material.attenuationDistance || 0.5, min: 0, max: 10, step: 0.01 },
                attenuationColor: material.attenuationColor ? `#${material.attenuationColor.getHexString()}` : '#ffffff',
                // bg: material.color ? `#${material.color.getHexString()}` : '#839681'
                sheen: { value: material.sheen || 0, min: 0, max: 1, step: 0.01 },
                sheenRoughness: { value: material.sheenRoughness || 1, min: 0, max: 1, step: 0.01 },
                sheenColor: material.sheenColor ? `#${material.sheenColor.getHexString()}` : '#000000',
            };
        }

        // Subsurface scattering material
        if (material.name.endsWith('_SSS')) {
            return {
                subSurfaceScattering: false,

                diffuse: { value: material.uniforms.diffuse.value },
                thicknessColor: { value: material.uniforms.thicknessColor.value },
                distortion: { value: material.uniforms.thicknessDistortion.value, min: 0.01, max: 1, step: 0.01 },
                ambient: { value: material.uniforms.thicknessAmbient.value, min: 0.01, max: 5, step: 0.05 },
                attenuation: { value: material.uniforms.thicknessAttenuation.value, min: 0.01, max: 5, step: 0.05 },
                power: { value: material.uniforms.thicknessPower.value, min: 0.01, max: 16, step: 0.1 },
                scale: { value: material.uniforms.thicknessScale.value, min: 0.01, max: 50, step: 0.1 },
            }
        }

        return baseConfig;
    }

    return null;
}